import { type ReactNode, useState } from 'react';

import type React from 'react';
import { Button as BootstrapButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { actions, type ButtonAction } from './button.model';

export * from './DropdownActionButton';

export interface ButtonProps {
  visible?: boolean;
  text?: string;
  shorText?: string;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  outline?: boolean;
  icon?: React.ElementType;
  block?: boolean;
  children?: ReactNode;
  action?: ButtonAction;
  className?: string;
  onClick?: () => void;
  onlyIcon?: boolean;
  form?: string;
}

const EmptyIcon = () => <></>;

const getActionText = (
  action: ButtonAction | undefined,
  text: string | undefined,
  t: (key: string) => string
) => {
  if (!action || text || !actions.has(action)) {
    return text;
  }

  return t(`common.button.${actions.get(action)?.keyTranslate}`);
};

const Button = ({
  disabled,
  text,
  shorText,
  outline,
  block,
  icon = EmptyIcon,
  type = 'button',
  variant = 'secondary',
  action,
  children,
  className,
  onClick,
  visible = true,
  onlyIcon,
  form
}: ButtonProps) => {
  const [disabledState, setDisabledState] = useState(false);
  const { t } = useTranslation();
  const newVariant = outline ? `outline-${variant}` : variant;
  const actionIcon = action ? actions.get(action)?.icon : undefined;
  const Icon = actionIcon ?? icon;
  const newText = getActionText(action, text, t);
  const newSortText = action && actions.has(action) && !shorText ? newText : shorText;

  if (!visible) {
    return <></>;
  }

  const disableOnClick = () => {
    setDisabledState(true);
    setTimeout(() => {
      setDisabledState(false);
    }, 1000);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();

      if (type !== 'submit') {
        disableOnClick();
      }
    }
  };

  return (
    <BootstrapButton
      className={`${className || ''}${block ? ' w-100' : ''}`.trim()}
      disabled={disabled || disabledState}
      type={type}
      variant={newVariant}
      onClick={handleClick}
      form={form}>
      <>
        <Icon />
        {(newText || newSortText) && !onlyIcon && (
          <span>
            <span className="d-none d-sm-inline">
              <span className="ms-2">{newText}</span>
            </span>
            {newSortText && (
              <span className="d-inline d-sm-none">
                <span className="ms-2">{newSortText}</span>
              </span>
            )}
          </span>
        )}
        {children}
      </>
    </BootstrapButton>
  );
};

export { Button };
export default Button;
